import RepositoryInterface from './RepositoryInterface'

import axios from 'axios'
import moment from 'moment-timezone'

const stripTags = html => {
  var doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

/**
 * Private methods declarations
*/
const convertGen2NoteToGen3 = Symbol('convertGen2NoteToGen3')
const parseResponse = Symbol('parseResponse')
const mapFiltersToGen2 = Symbol('mapFiltersToGen2')
const mapOrderingToGen2 = Symbol('createOrdering')
/**
 * RemoteNotesGen2Repository is an implementation of the RepositoryInterface
 * meant to also be a viable implementation of RemoteNotesRepository
*/

export default class RemoteNoteGen2Repository extends RepositoryInterface {
  constructor () {
    super()
    const API_URL = process.env.VUE_APP_API_URL || ''
    const resource = 'notes'
    this.url = `${API_URL}/v2/${resource}`
    this.http = axios
  }

  async listRecords ({queries = null}) {
    const url = `${this.url}/retrieveNotes`
    const payload = {
      id: queries.filters.reference_id,
      page: queries.pagination.currentPage - 1,
      pageSize: queries.pagination.pageSize
    }
    const reference = {
      'id': queries.filters.reference_id,
      'label': queries.filters.reference_label,
      'domain': queries.filters.reference_domain
    }
    const filters = this[mapFiltersToGen2](queries.filters)
    const {orderBy, ascending} = this[mapOrderingToGen2](queries.ordering)
    const search = queries.search || undefined

    payload.advSearch = filters
    payload.orderBy = orderBy
    payload.ascending = ascending
    payload.searchString = search

    const response = await this.http.post(url, payload)

    if (!response.data.records || response.data.success === false) {
      throw new Error('Failed to list notes')
    }
    return this[parseResponse](response.data.records, payload, reference)
  }

  [mapFiltersToGen2] (filters) {
    if (!filters) return undefined

    const dateAddedFilter = filters.date_added
    let _filters
    if (dateAddedFilter) {
      const dateAddedFilterFormatted = dateAddedFilter.map(date => moment(date).format('YYYY-MM-DD'))
      _filters = {
        'fromDate': dateAddedFilterFormatted[0],
        'toDate': dateAddedFilterFormatted[1]
      }
    }

    return _filters
  }

  [mapOrderingToGen2] (ordering) {
    if (!ordering) return ({orderBy: undefined, ascending: undefined})
    let ascending
    let orderingMap = {
      'date_added': 'date',
      'creator.name': 'enteredBy',
      // TODO order by title isn't working in the api
      'title': 'title'
    }
    const orderBy = orderingMap[ordering.key]

    if (ordering.order) {
      ascending = ordering.order === 'asc'
    }

    return ({orderBy, ascending})
  }

  [parseResponse] (records, payload, reference) {
    const data =
        records
          // in Gen 2, when you click on the "New note" button it creates an "empty" note,
          // even if you never click the "Save" button. This causes the generation of many empty notes.
          .filter(n => n.title || n.content)
          .map(n => this[convertGen2NoteToGen3](n, reference))

    return {
      data: data,
      pagination: {
        pageSize: payload.pageSize,
        currentPage: payload.page + 1,
        nextPage: data.length ? payload.page + 2 : null
      }
    }
  }

  async readRecord ({id}) {
    const notes = await this.listRecords()
    return notes.data.find(n => n.id === id)
  }

  [convertGen2NoteToGen3] (note, reference) {
    // eg.: "Bruno Felicio :: britecore_bruno.felicio@britecore.com"
    const [name, username] = note.enteredBy.split(' :: ')
    const dateAdded = note.date ? moment.tz(note.date, 'MM/DD/YYYY hh:mm A', 'America/Chicago').format() : note.date_added
    const newNote = {
      id: note.id,
      title: note.title,
      body: note.contents,
      creator: {
        username,
        name
      },
      reference,
      date_added: dateAdded,
      is_locked: note.frozen || !note.editable,
      enteredBy: note.enteredBy
    }
    return newNote
  }

  /**
   * Returns a list of note history events
   * @param {uuid} note uuid
   * @returns {Array<Object>} list of history events
  */
  listHistory ({id}) {
    // TODO ?
    return []
  }

  async createRecord ({data}) {
    const createResponse = await this.http.post(`${this.url}/newNote`, {id: data.reference.id})
    const note = createResponse.data
    const contents = stripTags(data.body)
    const storeResponse = await this.http.post(`${this.url}/storeNote`, {
      id: note.id,
      enteredBy: note.enteredBy,
      title: data.title,
      contents
    })
    if (!storeResponse.data.success) {
      try {
        await this.deleteRecord({id: note.id})
      } catch (e) {
        // swallow delete error
        console.warn(e)
      }
      throw new Error('Failed to create note')
    }
    note.contents = contents
    return this[convertGen2NoteToGen3]({...note, ...data}, data.reference)
  }

  async updateRecord ({id, data}) {
    const contents = stripTags(data.body)
    const storeResponse = await this.http.post(`${this.url}/storeNote`, {
      id: id,
      enteredBy: data.enteredBy,
      title: data.title,
      contents
    })
    if (!storeResponse.data.success) {
      throw new Error('Failed to update note')
    }

    data.contents = contents
    return this[convertGen2NoteToGen3](data, data.reference)
  }

  async deleteRecord ({id}) {
    const url = `${this.url}/removeNote`.replace('v2', 'v1')
    const response = await this.http.post(url, {
      id
    })
    if (!response.data.success) {
      throw new Error('Failed to delete note')
    }
  }
}
